import { useState, useMemo, useCallback } from 'react';
import Fuse, { FuseOptionKeyObject } from 'fuse.js';

const DEFAULT_THRESHOLD = 0.3; // Default threshold (0 = exact, 1 = match anything)

export function useTableRowFuzzyFiltering<T extends Record<string, unknown>>(
  items: T[],
  visibleColumns: Record<string, boolean>,
  options?: {
    threshold?: number;
    maxResults?: number;
  },
) {
  // State
  const [fuzzyFilter, setFuzzyFilter] = useState<string>('');
  const [threshold] = useState<number>(DEFAULT_THRESHOLD);

  // Memoized keys
  const keys = useMemo(() => {
    return Object.keys(visibleColumns)
      .filter((key) => visibleColumns[key])
      .map((key) => ({
        name: key,
        getFn: (item: T) =>
          typeof item[key as keyof T] === 'string'
            ? item[key as keyof T]
            : String(item[key as keyof T]),
      })) as FuseOptionKeyObject<T>[];
  }, [visibleColumns]);

  // This seem like it may be bad for performance on large datasets but good for now
  const fuse = useMemo(() => {
    const fuseOptions = {
      keys,
      threshold,
      ignoreLocation: true,
      includeScore: true,
    };

    return new Fuse(items, fuseOptions);
  }, [items, keys, threshold]);

  // Fuzzy filter
  const fuzzyFilteredItems = useMemo(() => {
    if (!fuzzyFilter.trim()) return items;

    const searchResults = fuse.search(fuzzyFilter);
    const maxResults = options?.maxResults || searchResults.length;

    // Return just the items (not the scores)
    return searchResults.slice(0, maxResults).map((result) => result.item);
  }, [items, fuzzyFilter, fuse, options?.maxResults]);

  // Set fuzzy filter
  const setFilter = useCallback((value: string) => {
    setFuzzyFilter(value);
  }, []);

  // Clear fuzzy filter
  const clearFilters = useCallback(() => {
    setFuzzyFilter('');
  }, []);

  return {
    fuzzyFilter,
    fuzzyFilteredItems,
    setFuzzyFilter,
    setFilter,
    clearFilters,
  };
}
